/**
 * Texte der Eingangsrechnungen im Web (Plan „Rollen am Geschäftspartner und Lieferantenverträge“, Aufgabe 17; P5): eine
 * Tabelle mit englischen Schlüsseln, kein Text im JSX. Die Fehlersätze der API stehen in der Domäne
 * (`incomingInvoiceRules.ts`, `supplierRules.ts`); die zwei, die das Web VOR dem Speichern selbst zeigt (Dublette am
 * Feld, Betragsform), stehen hier, weil web die Domäne nicht importiert.
 */
import { PAYMENT_STATE_TEXT } from "../suppliers/paymentState.js";

const invoices = (n: number): string => (n === 1 ? "1 Rechnung" : `${String(n)} Rechnungen`);

export const INCOMING_TEXT_WEB = {
  tab: "Eingangsrechnungen",
  area: "Eingangsrechnungen",
  /** Kurzlabel des Reiters am Telefon (Entscheid 522); steckt im langen Namen (Label in Name). */
  areaShort: "Rechnungen",
  record: "Eingangsrechnung erfassen",
  /** Ticket aus der Eingangsrechnung (Spec „Glocke“, Aufgabe 22); die Beschriftung des Bezugs wie im Ticket (Aufgabe 20). */
  createTicket: "Ticket anlegen",
  ticketReference: (entry: string, number: string): string => `Eingangsrechnung ${entry} · ${number}`,
  edit: "Bearbeiten",
  editTitle: (label: string): string => `${label} bearbeiten`,
  sectionHead: "Kopf",
  sectionAmounts: "Beträge",
  sectionFile: "Datei",
  split: "Aufteilen",
  contract: "Vertrag",
  noContract: "ohne Vertrag",
  number: "Rechnungsnummer",
  invoiceDate: "Rechnungsdatum",
  receivedOn: "Eingang",
  servicePeriod: "Leistungszeitraum",
  servicePeriodFrom: "Leistungszeitraum von",
  servicePeriodTo: "Leistungszeitraum bis",
  dueDate: "Fälligkeit",
  kindLabel: "Art",
  paymentMethod: "Zahlweise",
  suggested: "Vorgeschlagene Posten",
  /** Weitere offene Posten, später fällig als Rechnungsdatum und Leistungsende (A-S1). */
  moreCharges: (n: number): string => `weitere (${String(n)})`,
  credited: "Angerechnete Abschläge",
  payable: "Zahlbetrag",
  sum: "Summe",
  net: "Netto",
  taxCaseLabel: "Steuerfall",
  taxRate: "Steuersatz",
  tax: "Steuer",
  noTaxRate: "–",
  none: "–",
  pending: "…",
  removeLine: (n: number): string => `Betragszeile ${String(n)} entfernen`,
  kind: { INVOICE: "Rechnung", SETTLEMENT: "Jahresabrechnung", CREDIT_NOTE: "Gutschrift" },
  // Steuerfälle und Zahlweisen: `SUPPLIER_TEXT_WEB.taxCase`/`paymentMethod` (eine Tabelle für Vertrag und Rechnung, A-K3).
  /** „Zu zahlen bis 05.11.2026: 2 Rechnungen, 1.259,38 €“ */
  payableUntil: (datum: string, n: number, summe: string): string => `Zu zahlen bis ${datum}: ${invoices(n)}, ${summe}`,
  confirmCancel: (label: string): string => `${label} stornieren?`,
  cancelConsequence: "Gebundene Posten werden wieder offen; die Eingangsrechnung bleibt als storniert stehen.",
  cancelReason: "Grund",
  cancelAction: "Stornieren",
  payAction: "Zahlung bestätigen",
  // Die Frage selbst (Satz, Felder, Knöpfe, Fehlschlag) ist die gemeinsame der Lieferantenansichten (SUPPLIER_TEXT_WEB).
  save: "Speichern",
  cancel: "Abbrechen",
  fileAfterSave: "Erst speichern, dann Datei ablegen",
  savedHint: (label: string): string => `Gespeichert als ${label} — jetzt die Rechnung als PDF ablegen.`,
  withoutAccount: "ohne Konto",
  empty: "Keine Eingangsrechnungen.",
  dueUntil: "fällig bis",
  loading: "Lädt …",
  loadFailed: "Die Eingangsrechnungen konnten nicht geladen werden.",
  formDataFailed: "Verträge und Vorschläge konnten nicht geladen werden.",
  chargesLoadFailed: "Die gebundenen Posten konnten nicht geladen werden.",
  /** Dieselben Sätze wie die Domäne (`SUPPLIER_TEXT.duplicateInvoice`) — hier VOR dem Speichern am Feld. */
  duplicate: (entry: number): string => `Diese Rechnung ist schon erfasst: E ${String(entry)}.`,
  numberRequired: "Bitte die Rechnungsnummer des Lieferanten eintragen.",
  dateRequired: "Bitte ein Datum eintragen.",
  amountInvalid: "Ein Betrag hat höchstens zehn Stellen vor und zwei nach dem Komma.",
  // --- Liste ---
  /** Das „⋯“ an der Rechnung (Anatomie Teil 15): sein zugänglicher Name. */
  actionsFor: (number: string): string => `Aktionen zu Eingangsrechnung ${number}`,
  /** Das Objektpanel (F13): 404 und andere Ladefehler. */
  missing: "Diese Eingangsrechnung gibt es nicht (mehr), oder sie ist nicht sichtbar.",
  loadOneFailed: "Die Eingangsrechnung konnte nicht geladen werden.",
  /** Der Satz über einer Rechnung, die ein Sprung aus dem Bereich aufgeschlagen hat (A-W2). */
  jumpFromArea: "Aus dem Bereich Eingangsrechnungen geöffnet.",
  lines: "Positionen",
  charges: "Gebundene Posten",
  files: "Dateien",
  paid: (betrag: string, datum: string): string => `bezahlt ${betrag} am ${datum}`,
  cancelled: (grund: string): string => `Storniert: ${grund}`,
  // --- Bereich ---
  allStates: "Alle Zahlstatus",
  statesLabel: "Eingangsrechnungen nach Zahlstatus",
  companiesLabel: "Eingangsrechnungen nach Unternehmen",
  /** Die Chips des Bereichs: die Wörter des Kennzeichens (`PAYMENT_STATE_TEXT`, A-K3) — nur „angenommen bezahlt“ steht
   *  dort ausgeschrieben, weil der Chip keinen Zusatz „angenommen“ trägt. */
  stateChips: { ...PAYMENT_STATE_TEXT, ASSUMED_PAID: "angenommen bezahlt" },
} as const;
